/**
 * LLMChatDialog Component
 *
 * A reusable chat interface component for LLM interactions.
 * Provides a conversation-style UI with support for follow-up questions.
 */

import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';
import Markdown from 'react-markdown';
import type {Components} from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ChatChart from './ChatChart';
import LlmService from '../../services/llm.service';
import {Comment, CommentRole, ConversationHistory} from '../../@types/llm-conversation';

/**
 * Markdown component overrides for assistant messages. A fenced block with the
 * language "chart" (see CHAT_CHART_PROMPT in ChatChart) is rendered as a real
 * chart; every other code block/span keeps the default rendering.
 */
const markdownComponents: Components = {
    code: ({className, children}): React.JSX.Element => {
        const language = /language-(\w+)/.exec(className ?? '')?.[1];
        if (language === 'chart') {
            return <ChatChart source={String(children)} />;
        }
        return <code className={className}>{children}</code>;
    }
};

/**
 * Message structure for the chat (simplified to match backend)
 */
export interface ChatMessage {
    role: 'user' | 'assistant' | 'system';
    content: string;
}

/**
 * Props for LLMChatDialog component
 */
export interface LLMChatDialogProps {
    /**
     * Initial system prompt to start the conversation
     */
    initialPrompt?: string;
    /**
     * Title for the chat dialog
     */
    title?: string;
    /**
     * Callback when a new message is sent
     */
    onMessageSent?: (message: string) => void;
    /**
     * Maximum height of the entire component
     */
    maxHeight?: string | number;

    /**
     * Minimum height of the entire component
     * Required to prevent Chat body from collapsing when used in a DialogContent (Zak)
     */
    minHeight?: string | number;

    /**
     * Initial context message to show before conversation
     */
    contextMessage?: string;
    /**
     * Event sequence ID for context
     */
    eventSequenceId?: number;
    /**
     * Include ESD in context
     */
    includeESD?: boolean;
    /**
     * Filter function to determine which messages to render
     * Returns true to render, false to hide
     * If not provided, all messages are rendered
     */
    messageRenderFilter?: (message: ChatMessage, index: number) => boolean;
    /**
     * Context to prepend to the first user message (and after reset)
     * This allows priming the chat with context without auto-sending
     */
    contextPrepend?: string;
    /**
     * Custom actions to render in the header (to the right of the title)
     */
    headerActions?: React.ReactNode;
    /**
     * Callback when the close button is clicked (shows close button if provided)
     */
    onClose?: () => void;
    /**
     * When true, fills the parent container height instead of using maxHeight
     */
    fillHeight?: boolean;
    /**
     * Message to inject and automatically send. When this changes to a non-empty value,
     * the message will be added to the conversation and sent to the LLM.
     */
    injectedMessage?: string;
    /**
     * Callback when injected message has been processed (sent or cleared)
     */
    onInjectedMessageProcessed?: () => void;
    /**
     * Controlled mode: external messages array. When provided, component uses this instead of internal state.
     */
    messages?: ChatMessage[];
    /**
     * Controlled mode: callback when messages change. Required when messages prop is provided.
     */
    onMessagesChange?: (messages: ChatMessage[]) => void;
    /**
     * Controlled mode: external context sent flag
     */
    contextSent?: boolean;
    /**
     * Controlled mode: callback when context sent changes
     */
    onContextSentChange?: (sent: boolean) => void;
    /**
     * Extra right padding for header icons (use when wrapped in FullscreenWrapper to avoid overlap)
     */
    headerIconsPaddingRight?: number;
    /**
     * Optional example questions shown as clickable chips while the conversation is empty.
     * Clicking a chip submits it as the first user message.
     */
    suggestedPrompts?: string[];
}

/**
 * Reusable LLM Chat Dialog Component
 */
const LLMChatDialog: React.FC<LLMChatDialogProps> = ({
    initialPrompt,
    title = 'AI Assistant',
    onMessageSent,
    minHeight,
    maxHeight = 500,
    contextMessage = 'Analyzing data...',
    eventSequenceId = 0,
    includeESD = false,
    messageRenderFilter,
    contextPrepend,
    headerActions,
    onClose,
    fillHeight = false,
    injectedMessage,
    onInjectedMessageProcessed,
    messages: controlledMessages,
    onMessagesChange,
    contextSent: controlledContextSent,
    onContextSentChange,
    headerIconsPaddingRight = 0,
    suggestedPrompts
}) => {
    // Determine if we're in controlled mode
    const isControlled = controlledMessages !== undefined && onMessagesChange !== undefined;

    // Internal state (used when not in controlled mode)
    const [internalMessages, setInternalMessages] = useState<ChatMessage[]>([]);
    const [internalContextSent, setInternalContextSent] = useState(false);

    // Unified state access - use controlled or internal based on mode
    const messages = isControlled ? controlledMessages : internalMessages;
    const setMessages = isControlled
        ? onMessagesChange
        : setInternalMessages;
    const contextSent = isControlled && controlledContextSent !== undefined
        ? controlledContextSent
        : internalContextSent;
    const setContextSent = isControlled && onContextSentChange
        ? onContextSentChange
        : setInternalContextSent;

    // Other internal state (always internal)
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isInitialized, setIsInitialized] = useState(false);

    // Refs
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Track previous message count to detect new messages vs initial load
    const prevMessageCountRef = useRef<number>(0);

    // Auto-scroll to bottom only when NEW messages are added (not on initial load/tab switch).
    // Use scrollTop directly on the container ref rather than scrollIntoView — the latter
    // propagates to ALL scrollable ancestors and would scroll the outer workbook container,
    // pushing the entire panel off-screen.
    const scrollToBottom = (): void => {
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
    };

    useEffect(() => {
        // Only scroll if messages were added (count increased), not on initial load
        if (messages.length > prevMessageCountRef.current && prevMessageCountRef.current > 0) {
            scrollToBottom();
        }
        prevMessageCountRef.current = messages.length;
    }, [messages.length]);

    // Initialize conversation with initial prompt
    // Skip if in controlled mode and messages already exist (persisted state)
    useEffect(() => {
        if (initialPrompt && !isInitialized) {
            // In controlled mode, skip initialization if messages already exist
            if (isControlled && messages.length > 0) {
                setIsInitialized(true);
                return;
            }

            setIsInitialized(true);

            // Start with context message
            const contextMsg: ChatMessage = {
                role: 'system',
                content: contextMessage
            };

            // Initial user message (the prompt)
            const userMsg: ChatMessage = {
                role: 'user',
                content: initialPrompt
            };

            setMessages([contextMsg, userMsg]);

            // Send to LLM (this is the first message, so context will be prepended)
            sendConversation([contextMsg, userMsg], true);
        }
    }, [initialPrompt, isInitialized, contextMessage]);

    // In controlled mode, reset isInitialized when switching to a node with no history
    // This allows the initialization effect to run for the new node
    useEffect(() => {
        if (isControlled && messages.length === 0 && isInitialized) {
            setIsInitialized(false);
        }
    }, [isControlled, messages.length, isInitialized]);

    // Handle injected message - supports plain text or structured JSON messages
    useEffect(() => {
        if (injectedMessage && !isLoading) {
            // Try to parse as structured message
            let structured: { type: string; message?: ChatMessage; status?: ChatMessage; result?: ChatMessage } | null = null;
            try {
                structured = JSON.parse(injectedMessage);
            } catch {
                // Not JSON, treat as plain text message
            }

            if (structured?.type === 'status') {
                // Status message - just add to messages without sending to LLM
                if (structured.message) {
                    // Replace any existing status message or add new one
                    const withoutPreviousStatus = messages.filter((m: ChatMessage) =>
                        !(m.role === 'system' && (
                            m.content.startsWith('Querying Daedalus') ||
                            m.content.startsWith('CFR lookup failed')
                        ))
                    );
                    setMessages([...withoutPreviousStatus, structured.message]);
                }
                onInjectedMessageProcessed?.();
            } else if (structured?.type === 'progress') {
                // Progress step - appended to the log without removing previous messages
                if (structured.message) {
                    setMessages([...messages, structured.message]);
                }
                onInjectedMessageProcessed?.();
            } else if (structured?.type === 'cfr_result') {
                // CFR result - add final status and assistant response
                // Remove any intermediate status messages
                const withoutStatus = messages.filter((m: ChatMessage) =>
                    !(m.role === 'system' && m.content.startsWith('Querying Daedalus'))
                );
                const newMessages = [...withoutStatus];
                if (structured.status) {
                    newMessages.push(structured.status);
                }
                if (structured.result) {
                    newMessages.push(structured.result);
                }
                setMessages(newMessages);
                onInjectedMessageProcessed?.();
            } else {
                // Plain text message - store without context, context added in API call
                const isFirstMessage = !contextSent && messages.filter(m => m.role === 'user').length === 0;

                const newUserMsg: ChatMessage = {
                    role: 'user',
                    content: injectedMessage
                };

                const updatedMessages = [...messages, newUserMsg];
                setMessages(updatedMessages);
                sendConversation(updatedMessages, isFirstMessage);
                onInjectedMessageProcessed?.();
            }
        }
    }, [injectedMessage]);

    // Send the full conversation to LLM and get response
    const sendConversation = async (conversationMessages: ChatMessage[], isFirstMessage: boolean = false): Promise<void> => {
        setIsLoading(true);
        setError(null);

        try {
            // Convert our messages to the backend format
            // Filter out system messages for the API (they're just for UI context)
            const nonSystemMessages = conversationMessages.filter(m => m.role !== 'system');

            // Prepend the context to the first user message on EVERY call: the backend
            // is stateless and each request must carry the full conversation, so leaving
            // the context out of follow-up calls would make the model lose it entirely.
            // The stored history keeps the raw message; the context exists only in the payload.
            const comments: Comment[] = nonSystemMessages.map((m, index) => {
                let content = m.content;

                if (contextPrepend && m.role === 'user' && index === 0) {
                    content = contextPrepend + m.content;
                }

                return {
                    role: m.role === 'user' ? CommentRole.USER : CommentRole.ASSISTANT,
                    content,
                    timestamp: new Date().toISOString()
                };
            });

            // No llm field: the backend selects the model and reports it in the response
            const conversation: ConversationHistory = {
                eventSequenceId,
                includeESD,
                comments,
                queryTime: new Date().toISOString(),
                error: null as any
            };

            const response = await LlmService.postLlmConversation(conversation);

            if (response.data && response.data.comments && Array.isArray(response.data.comments)) {
                // The response contains the full conversation including the assistant's response
                // Extract just the assistant's last response
                const responseComments = response.data.comments;
                const lastComment = responseComments[responseComments.length - 1];

                if (lastComment && (lastComment.role === 'ASSISTANT' || lastComment.role === 'SYSTEM' || lastComment.role === 'assistant' || lastComment.role === 'system')) {
                    // Add assistant's response to our messages
                    const assistantMsg: ChatMessage = {
                        role: 'assistant',
                        content: lastComment.content
                    };

                    setMessages([...conversationMessages, assistantMsg]);

                    // Mark context as sent after first successful call
                    if (isFirstMessage && !contextSent) {
                        setContextSent(true);
                    }
                } else {
                    throw new Error('No assistant response in API response');
                }
            } else {
                throw new Error('Invalid response format from API');
            }

        } catch (err: any) {
            // eslint-disable-next-line no-console
            console.error('Failed to send message:', err);
            setError(err.response?.data?.message || err.message || 'Failed to get response');
        } finally {
            setIsLoading(false);
        }
    };

    // Submit a message — shared by the input box and the suggested-prompt chips
    const submitMessage = (text: string): void => {
        const trimmed = text.trim();
        if (!trimmed || isLoading) return;

        // Determine if this is the first user message (context not yet sent)
        const isFirstMessage = !contextSent && messages.filter(m => m.role === 'user').length === 0;

        // Store just the user's message - context is prepended only in the API call
        const newUserMsg: ChatMessage = {
            role: 'user',
            content: trimmed
        };

        // Add user message to conversation
        const updatedMessages = [...messages, newUserMsg];
        setMessages(updatedMessages);

        // Send the full conversation (context prepended in sendConversation for first message only)
        sendConversation(updatedMessages, isFirstMessage);

        // Callback
        onMessageSent?.(trimmed);
    };

    // Handle send button click
    const handleSend = (): void => {
        if (inputValue.trim() && !isLoading) {
            submitMessage(inputValue);
            setInputValue('');
        }
    };

    // Handle Enter key press
    const handleKeyPress = (e: React.KeyboardEvent): void => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    // Reset conversation
    const handleReset = (): void => {
        setMessages([]);
        setError(null);
        setIsInitialized(false);
        setInputValue('');
        setContextSent(false);

        // Re-initialize with initial prompt if available
        if (initialPrompt) {
            const contextMsg: ChatMessage = {
                role: 'system',
                content: contextMessage
            };

            const userMsg: ChatMessage = {
                role: 'user',
                content: initialPrompt
            };

            setMessages([contextMsg, userMsg]);
            sendConversation([contextMsg, userMsg], true);
            setIsInitialized(true);
        }
    };

    // Prepare messages for rendering
    const messagesToRender = messages.map((msg, index) => ({
        ...msg,
        shouldRender: messageRenderFilter ? messageRenderFilter(msg, index) : true,
        index
    }));

    // Whether the user has started a conversation (ignoring system context messages).
    // Drives the suggested-prompt chips, which only show before the first exchange.
    const hasConversation = messages.some(m => m.role !== 'system');

    // Allow right-click context menu (prevent parent canvas from capturing it)
    const handleContextMenu = useCallback((e: React.MouseEvent): void => {
        e.stopPropagation();
    }, []);

    return (
        <Card
            onContextMenu={handleContextMenu}
            sx={{
                ...(fillHeight ? { flex: 1, minHeight: 0 } : { maxHeight }),
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}
        >
            <CardContent sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                p: 1.5,
                overflow: 'hidden',
                minHeight: minHeight || 0,
                '&:last-child': { pb: 1.5 }
            }}>
                {/* Header */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <AutoAwesomeIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        <Typography variant="subtitle2" fontWeight="medium">
                            {title}
                        </Typography>
                        {headerActions}
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ pr: headerIconsPaddingRight }}>
                        {messages.length > 0 && (
                            <IconButton size="small" onClick={handleReset} title="Clear conversation">
                                <RefreshIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        )}
                        {onClose && (
                            <IconButton size="small" onClick={onClose} title="Close">
                                <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        )}
                    </Stack>
                </Stack>

                <Divider sx={{ mb: 1 }} />

                {/* Messages Area */}
                <Box
                    ref={messagesContainerRef}
                    sx={{
                        flexGrow: 1,
                        flexShrink: 1,
                        flexBasis: 0,
                        height: 0,
                        overflow: 'auto',
                        mb: 1,
                        px: 1,
                        '&::-webkit-scrollbar': {
                            width: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: 'grey.100',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'grey.400',
                            borderRadius: '3px',
                        },
                    }}
                >
                    {messagesToRender.map((message) => {
                        // For hidden user messages, show a small indicator
                        if (!message.shouldRender && message.role === 'user' && message.index === 1) {
                            return (
                                <Box key={message.index} sx={{ textAlign: 'center', my: 0.5 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: 'text.secondary',
                                            fontStyle: 'italic',
                                            fontSize: '0.65rem'
                                        }}
                                    >
                                        [Initial analysis request sent to AI]
                                    </Typography>
                                </Box>
                            );
                        }

                        // Skip rendering if filter returns false
                        if (!message.shouldRender) {
                            return null;
                        }

                        return (
                            <Box key={message.index} sx={{ mb: 1.5 }}>
                                {message.role === 'system' ? (
                                    // System context message
                                    <Box sx={{ textAlign: 'center', my: 1 }}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: 'text.secondary',
                                                fontStyle: 'italic',
                                                bgcolor: 'grey.100',
                                                px: 2,
                                                py: 0.5,
                                                borderRadius: 1,
                                                display: 'inline-block'
                                            }}
                                        >
                                            {message.content}
                                        </Typography>
                                    </Box>
                                ) : (
                                    // User and Assistant messages
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="flex-start"
                                        sx={{
                                            justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start'
                                        }}
                                    >
                                        {message.role === 'assistant' && (
                                            <SmartToyIcon sx={{ fontSize: 20, color: 'primary.main', mt: 0.5 }} />
                                        )}

                                        <Paper
                                            elevation={1}
                                            sx={{
                                                px: 1.5,
                                                py: 1,
                                                maxWidth: '85%',
                                                bgcolor: message.role === 'user' ? 'primary.50' : 'grey.50',
                                                borderRadius: 2,
                                                borderTopLeftRadius: message.role === 'assistant' ? 0 : 2,
                                                borderTopRightRadius: message.role === 'user' ? 0 : 2,
                                            }}
                                        >
                                            {message.role === 'assistant' ? (
                                                <Box sx={{
                                                    fontSize: '0.75rem',
                                                    lineHeight: 1.5,
                                                    '& p': { margin: '0.5em 0' },
                                                    '& p:first-of-type': { marginTop: 0 },
                                                    '& p:last-of-type': { marginBottom: 0 },
                                                    '& h1, & h2, & h3, & h4': {
                                                        fontSize: '0.85rem',
                                                        fontWeight: 600,
                                                        margin: '0.75em 0 0.5em 0'
                                                    },
                                                    '& h2:first-of-type': { marginTop: 0 },
                                                    '& ul, & ol': {
                                                        margin: '0.5em 0',
                                                        paddingLeft: '1.5em'
                                                    },
                                                    '& li': { margin: '0.25em 0' },
                                                    '& strong': { fontWeight: 600 },
                                                    '& em': { fontStyle: 'italic' },
                                                    '& code': {
                                                        backgroundColor: 'grey.200',
                                                        padding: '0.1em 0.3em',
                                                        borderRadius: '3px',
                                                        fontSize: '0.7rem'
                                                    },
                                                    '& blockquote': {
                                                        borderLeft: '3px solid',
                                                        borderColor: 'grey.400',
                                                        paddingLeft: '0.75em',
                                                        margin: '0.5em 0',
                                                        fontStyle: 'italic',
                                                        color: 'text.secondary'
                                                    },
                                                    // Table styles for GFM tables
                                                    '& table': {
                                                        borderCollapse: 'collapse',
                                                        width: '100%',
                                                        margin: '0.75em 0',
                                                        fontSize: '0.7rem'
                                                    },
                                                    '& th, & td': {
                                                        border: '1px solid',
                                                        borderColor: 'grey.300',
                                                        padding: '0.4em 0.6em',
                                                        textAlign: 'left'
                                                    },
                                                    '& th': {
                                                        backgroundColor: 'grey.100',
                                                        fontWeight: 600
                                                    },
                                                    '& tr:nth-of-type(even)': {
                                                        backgroundColor: 'grey.50'
                                                    }
                                                }}>
                                                    <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{message.content}</Markdown>
                                                </Box>
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontSize: '0.75rem',
                                                        whiteSpace: 'pre-wrap',
                                                        lineHeight: 1.5
                                                    }}
                                                >
                                                    {message.content}
                                                </Typography>
                                            )}
                                        </Paper>

                                        {message.role === 'user' && (
                                            <PersonIcon sx={{ fontSize: 20, color: 'text.secondary', mt: 0.5 }} />
                                        )}
                                    </Stack>
                                )}
                            </Box>
                        );
                    })}

                    {/* Suggested prompts — only before the first exchange */}
                    {suggestedPrompts && suggestedPrompts.length > 0 && !hasConversation && !isLoading && (
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                                Try asking:
                            </Typography>
                            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                                {suggestedPrompts.map((prompt) => (
                                    <Chip
                                        key={prompt}
                                        label={prompt}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        clickable
                                        onClick={(): void => submitMessage(prompt)}
                                        sx={{
                                            fontSize: '0.7rem',
                                            height: 'auto',
                                            py: 0.5,
                                            '& .MuiChip-label': { whiteSpace: 'normal' }
                                        }}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {/* Loading indicator */}
                    {isLoading && (
                        <Box sx={{ mb: 1.5 }}>
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="flex-start"
                            >
                                <SmartToyIcon sx={{ fontSize: 20, color: 'primary.main', mt: 0.5 }} />
                                <Paper
                                    elevation={1}
                                    sx={{
                                        px: 1.5,
                                        py: 1,
                                        bgcolor: 'grey.50',
                                        borderRadius: 2,
                                        borderTopLeftRadius: 0,
                                    }}
                                >
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <CircularProgress size={14} />
                                        <Typography variant="caption" color="text.secondary">
                                            Thinking...
                                        </Typography>
                                    </Stack>
                                </Paper>
                            </Stack>
                        </Box>
                    )}

                </Box>

                {/* Error Alert */}
                {error && (
                    <Alert severity="error" sx={{ mb: 1, py: 0 }}>
                        <Typography variant="caption">{error}</Typography>
                    </Alert>
                )}

                {/* Input Area */}
                <TextField
                    ref={inputRef}
                    fullWidth
                    size="small"
                    placeholder="Type a follow-up question..."
                    value={inputValue}
                    onChange={(e): void => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={isLoading}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            fontSize: '0.75rem',
                        }
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    onClick={handleSend}
                                    disabled={!inputValue.trim() || isLoading}
                                    color="primary"
                                >
                                    <SendIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
            </CardContent>
        </Card>
    );
};

export default React.memo(LLMChatDialog);